import React from "react";
import { unstable_batchedUpdates } from "react-dom";
import authModule from "../../utilities/AuthModule";
import { logger } from "../../utilities/logger";
import { runWithConcurrency } from "../../utilities/ConcurrencyPool";
import { rowLockIndicator, SharePointFolderContentTypeIds } from "../../src/taskpane/config/constants";
import { AggregatedFailureReason, classifyAggregatedFailure } from "../Services/aggregatedFailureReason";
import { AGGREGATED_MAX_TOTAL_ROWS, resolveAggregatedPageSize, resolveGroupSourcePageSize } from "../Services/aggregatedPageSize";
import {
    AggregatedSortRank,
    IAggregatedColumn,
    IAggregatedColumnBinding,
    IAggregatedCursorState,
    IAggregatedSortSpec,
    IAggregatedViewOrders,
    ILibraryFieldSet,
    compareAggregateRowKeys,
    compareSortRanks,
    countEmittableRows,
    getAggregatedRowKey,
    getFrontierListIds,
    isNumericRankType,
    getViewHiddenColumnKeys,
    mergeAggregatedColumns,
    orderColumnsByViewStats,
    parseLibraryField,
    pinAggregatedColumnOrder,
    resolveColumnSortBindings,
    toBindingRank,
    toSortValue,
    withNonVotingViewOrders,
} from "../../utilities/AggregatedColumnHelper";
import {
    IAggregatedLibraryDescriptor,
    IAggregatedPageOptions,
    buildAggregatedViewXml,
    fetchAggregatedDocumentsPage,
    fetchAggregatedLibraryFields,
    fetchAggregatedLibraryViewFields,
    fetchAggregatedRowById,
    recycleAggregatedItem,
} from "../Services/AggregatedDocumentsService";
import { dedupeAggregatedSourceDescriptors, getAggregatedSourceKey } from "../Services/aggregatedSourceDescriptors";
import { clearClientGroupLibraryFieldsCache, readClientGroupLibraryFields } from "../Services/clientGroupLibraryFieldsCache";
import { markAggregatedLoadStart, measureAggregatedLoad } from "../Services/aggregatedLoadTiming";
import { buildLibraryWhereXml } from "./aggregatedFilterCaml";
import {
    AGGREGATE_ROW_KEY,
    AGGREGATE_SORT_VALUE,
    AGGREGATE_SOURCE_KEY,
    AGGREGATE_SOURCE_LIBRARY_NAME,
    AGGREGATE_SOURCE_LIST_ID,
    AGGREGATE_SOURCE_SITE_NAME,
    AGGREGATE_SOURCE_WEB_URL,
} from "./helpers/aggregateColumnRenderers";

/**
 * State machine for the cross-library "All documents" grid.
 *
 * Load: fan out field-schema requests per library (bounded pool, cached per library
 * set) → merge columns by display name → fan out first RenderListDataAsStream pages
 * (server-sorted by the active sort's per-library binding, optionally filtered by the
 * cross-library search value) → k-way merge the per-list cursors, emitting only rows no
 * unfetched page could still precede. Load-more advances only the cursor(s) blocking
 * the merge frontier.
 *
 * Robustness: every async result is guarded by a request sequence (stale responses are
 * dropped), and per-library failures are isolated — a 403/timeout on one library
 * reports that library instead of failing the view.
 *
 * Client-group libraries (`groupLibraries`, the libraries of the sites grouped with this one)
 * JOIN every load. Their field lists are read as soon as they are known (session-cached, see
 * clientGroupLibraryFieldsCache), and their first pages alongside the site's own first pages:
 * those need only the site's own field lists, which decide the sort mode
 * (resolveColumnSortBindings), the column order (pinned) and column visibility (group
 * libraries do not vote, see withNonVotingViewOrders). The first paint then waits up to
 * GROUP_JOIN_HOLD_MS for them, so the view usually paints once and nothing moves. When they
 * are later, the site's own rows show and the join merges in after them: before publishing
 * it pages the group cursors that would pull the frontier above rows already shown, so those
 * rows stay on screen. Every sort, search, filter and refresh repeats this.
 */

const FETCH_CONCURRENCY = 4;
// The same limit Advanced Search uses for its SharePoint calls.
const GROUP_FETCH_CONCURRENCY = 6;
/**
 * How long the first paint of a load waits for the client group's rows once the site's own are
 * ready (the shimmer stays up meanwhile). In time, the view paints once; later, the site's own
 * rows show and the group's join after them.
 */
export const GROUP_JOIN_HOLD_MS = 2000;
// Rows a group join keeps on screen: the rows the grid has actually rendered (on screen plus
// Fluent's overscan, see markRowsRendered), at least a screenful and at most the maximum; and
// how many rounds of paging it may spend on that before publishing anyway (the rest refills
// through load-more). Keeping only rendered rows spends no requests on rows nobody has seen.
export const GROUP_JOIN_KEEP_ROWS = 200;
const GROUP_JOIN_MIN_KEEP_ROWS = 30;
const GROUP_JOIN_MAX_ROUNDS = 3;
// Defined alongside the page-size policy (the two are one budget) and re-exported here
// so existing importers are unaffected.
export { AGGREGATED_MAX_TOTAL_ROWS } from "../Services/aggregatedPageSize";

/** Column key of the default (Modified desc) sort — matches the grid's Modified column. */
export const AGGREGATED_DEFAULT_SORT_KEY = "aggModified";

const AGGREGATE_ROW_RANK = "__aggregateRank";

export interface IAggregatedLibraryFailure {
    // Failure identity — a listId alone is ambiguous once two sources share a list
    // (Tess folder dossiers), and deduping on it would silently drop one source's badge.
    sourceKey: string;
    listId: string;
    libraryName: string;
    /**
     * Why it dropped out. Carried so the view can say something ACTIONABLE about the
     * one cause the user can fix — a library over the list view threshold needs an
     * index on the sorted/filtered column — instead of folding it into the same
     * "could not load" as a 403 or a timeout.
     */
    reason: AggregatedFailureReason;
}

export interface IAggregatedSortState {
    columnKey: string;
    descending: boolean;
}

/** One active column filter: match any of `values` (exact) and/or `containsText`. */
export interface IAggregatedColumnFilter {
    columnKey: string;   // merged column key (IAggregatedColumn.key)
    displayName: string;
    values: string[];
    containsText?: string;
}

// Filter translation rules (EQ vs Contains types, numeric normalization, library
// exclusion) live in the pure module aggregatedFilterCaml.ts so they are unit-testable
// without this module's auth chain; isFilterableColumn/isUnfilterableBinding live in
// AggregatedColumnHelper for the same reason. Re-exported for existing importers.
export { isFilterableColumn, isUnfilterableBinding } from "../../utilities/AggregatedColumnHelper";
export { buildLibraryWhereXml } from "./aggregatedFilterCaml";

interface ISortResolution {
    spec: IAggregatedSortSpec;
    // Per-list binding for the sorted column; undefined key = library lacks the column.
    bindings: Record<string, IAggregatedColumnBinding | undefined>;
    isDefault: boolean;
    // For fixed built-in sorts (name/editor) the internal name is identical everywhere.
    fixedInternalName?: string;
    // The merged column sorted by, when it is not a fixed sort: a group join extends the
    // bindings to its libraries through it.
    columnKey?: string;
}

interface ILibraryCursor {
    library: IAggregatedLibraryDescriptor;
    viewXml: string;
    pageOptions: IAggregatedPageOptions;
    nextHref: string | null;
    exhausted: boolean;
    lastFetchedRank: AggregatedSortRank;
    rows: any[];
    // A client-group library's cursor (joined by the second stage).
    isGroupSource?: boolean;
}

/** What the first stage of a load decided, for the group join of the same load. Published once
 * the site's own field lists are read, so the join can run alongside the site's own pages. */
interface IStageOneContext {
    sequence: number;
    fieldSets: ILibraryFieldSet[];
    viewOrders: IAggregatedViewOrders;
    primaryListIds: Set<string>;
    ownListIds: Set<string>;
    fetchedCount: number;
    // Rows the site's own first pages can bring (libraries × page size), for the group page size.
    plannedRows: number;
    excluded: string[];
    searchValue: string;
    filters: IAggregatedColumnFilter[];
    // Settles once the site's own first pages are in the merge (or failed): the join merges
    // after it, never into a half-loaded first stage. A superseded load's never settles; its
    // join is superseded with it.
    ownRowsReady: Promise<void>;
}

interface ISchemaCacheEntry {
    key: string;
    fieldSets: ILibraryFieldSet[];
    // Default-view field order per library; only fetch-succeeded libraries appear
    // (absence = fail-open, see AggregatedColumnHelper view stats).
    viewOrders: IAggregatedViewOrders;
    // ListIds only — descriptors are re-derived from the CURRENT library array on every
    // cache hit so late-resolving fields (driveId) never go stale inside the cache.
    readableListIds: string[];
    failures: IAggregatedLibraryFailure[];
}

export interface IUseAggregatedDocumentsResult {
    isLoading: boolean;
    isLoadingMore: boolean;
    /** Client-group libraries are still joining this load (the site's own rows are shown). */
    isLoadingGroupSources: boolean;
    /**
     * The grid reports how many rows it has rendered (the row count up to the last mounted
     * row). A client-group join keeps those rows on screen, and spends no requests on more.
     */
    markRowsRendered: (count: number) => void;
    rows: any[];
    mergedColumns: IAggregatedColumn[];
    // Columns NO known library default view displays — the host default-deselects
    // them (derived signal, never persisted; user toggles override).
    viewHiddenColumnKeys: Set<string>;
    hasMore: boolean;
    isCapped: boolean;
    failures: IAggregatedLibraryFailure[];
    excludedByFilter: string[];
    /**
     * Any loaded library has ForceCheckout on — half of the realtime gate the
     * single-library grid uses (Docubird's checkout/PDF windows broadcast item updates
     * for those libraries even when the tenant runs no automation hooks).
     */
    hasForceCheckoutLibrary: boolean;
    sort: IAggregatedSortState;
    setSortColumn: (columnKey: string) => void;
    searchValue: string;
    setSearchValue: (value: string) => void;
    filters: IAggregatedColumnFilter[];
    setFilters: (filters: IAggregatedColumnFilter[]) => void;
    loadMore: () => void;
    refresh: () => void;
    deleteItems: (items: any[]) => Promise<number>;
    refreshItems: (items: any[]) => Promise<void>;
    // Realtime (SignalR ItemHub) row mutations, each scoped to ONE source library.
    applyRealtimeCreate: (listId: string, itemId: string) => void;
    applyRealtimeUpdate: (listId: string, itemId: string) => void;
    applyRealtimeDelete: (listId: string, itemId: string) => void;
    applyRealtimeLock: (listId: string, itemId: string) => void;
}

// Built-in grid columns whose internal name is identical in every document library.
const FIXED_SORT_INTERNAL_NAMES: Record<string, { internalName: string; numeric: boolean }> = {
    aggName: { internalName: "FileLeafRef", numeric: false },
    aggEditor: { internalName: "Editor", numeric: false },
    [AGGREGATED_DEFAULT_SORT_KEY]: { internalName: "Modified", numeric: true },
};

const NO_GROUP_LIBRARIES: IAggregatedLibraryDescriptor[] = [];

// Client-group libraries that can join: valid, not one of the site's own lists, not overlapping.
const validGroupSources = (groupLibraries: IAggregatedLibraryDescriptor[], ownListIds: Set<string>): IAggregatedLibraryDescriptor[] =>
    dedupeAggregatedSourceDescriptors((groupLibraries ?? []).filter(library =>
        library?.listId && library?.webUrl && !ownListIds.has(String(library.listId).toLowerCase())));

const groupSchemaKey = (library: IAggregatedLibraryDescriptor): string => `${library.listId}:${library.webUrl}`;

// One source per list: field lists are per list, and two sources on one list share it.
const uniqueGroupLists = (groupSources: IAggregatedLibraryDescriptor[]): IAggregatedLibraryDescriptor[] =>
    groupSources.filter((library, index, all) => all.findIndex(other => groupSchemaKey(other) === groupSchemaKey(library)) === index);

// How a client-group library is named in the view's messages: by its site, then the library.
const groupSourceLabel = (library: IAggregatedLibraryDescriptor): string =>
    library.siteName ? `${library.siteName} › ${library.libraryName}` : library.libraryName;

/**
 * @param groupLibraries the client group's libraries, once known.
 * @param isReadingGroupLibraries the client group's libraries are still being read: the first
 *   paint holds for them too (see GROUP_JOIN_HOLD_MS).
 */
export const useAggregatedDocuments = (
    libraries: IAggregatedLibraryDescriptor[],
    groupLibraries: IAggregatedLibraryDescriptor[] = NO_GROUP_LIBRARIES,
    isReadingGroupLibraries: boolean = false
): IUseAggregatedDocumentsResult => {
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [isLoadingMore, setIsLoadingMore] = React.useState<boolean>(false);
    const [rows, setRows] = React.useState<any[]>([]);
    const [mergedColumns, setMergedColumns] = React.useState<IAggregatedColumn[]>([]);
    const [viewHiddenColumnKeys, setViewHiddenColumnKeys] = React.useState<Set<string>>(new Set());
    const [hasMore, setHasMore] = React.useState<boolean>(false);
    const [isCapped, setIsCapped] = React.useState<boolean>(false);
    const [failures, setFailures] = React.useState<IAggregatedLibraryFailure[]>([]);
    const [hasForceCheckoutLibrary, setHasForceCheckoutLibrary] = React.useState<boolean>(false);
    const [refreshToggle, setRefreshToggle] = React.useState<boolean>(false);
    const [sort, setSort] = React.useState<IAggregatedSortState>({ columnKey: AGGREGATED_DEFAULT_SORT_KEY, descending: true });
    const [searchValue, setSearchValue] = React.useState<string>("");
    const [filters, setFilters] = React.useState<IAggregatedColumnFilter[]>([]);
    const [excludedByFilter, setExcludedByFilter] = React.useState<string[]>([]);

    const filtersKey = React.useMemo(() => JSON.stringify(filters), [filters]);

    const requestSequence = React.useRef<number>(0);
    const cursorsRef = React.useRef<Map<string, ILibraryCursor>>(new Map());
    const isLoadingMoreRef = React.useRef<boolean>(false);
    const schemaCacheRef = React.useRef<ISchemaCacheEntry | null>(null);
    const sortResolutionRef = React.useRef<ISortResolution>({ spec: { descending: true, numeric: true }, bindings: {}, isDefault: true });

    // Client-group stage. The latest group libraries are read through a ref so the first stage
    // never re-runs because they arrived; their field lists come from the session cache
    // (clientGroupLibraryFieldsCache) and are kept here per list (null = failed in this view)
    // until a refresh, so later loads only re-read their pages.
    const groupLibrariesRef = React.useRef<IAggregatedLibraryDescriptor[]>(groupLibraries);
    groupLibrariesRef.current = groupLibraries;
    const isReadingGroupLibrariesRef = React.useRef<boolean>(isReadingGroupLibraries);
    isReadingGroupLibrariesRef.current = isReadingGroupLibraries;
    // The first paint of load `sequence`, held for the client group until `timer` (see
    // GROUP_JOIN_HOLD_MS). While it holds, nothing is emitted.
    const holdRef = React.useRef<{ sequence: number; timer: ReturnType<typeof setTimeout> } | null>(null);
    const groupSchemaCacheRef = React.useRef<Map<string, ILibraryFieldSet | null>>(new Map());
    // Set by a refresh: the field lists above are kept for the first stage's sort and columns, and
    // the next group join reads every one of them again.
    const groupSchemaStaleRef = React.useRef<boolean>(false);
    const stageOneRef = React.useRef<IStageOneContext | null>(null);
    const [stageOneSequence, setStageOneSequence] = React.useState<number>(0);
    const groupJoinSequenceRef = React.useRef<number>(0);
    // `${stageOneSequence}|${groupLibrariesKey}` of the last finished join.
    const [finishedJoin, setFinishedJoin] = React.useState<string>("");
    // The column order on screen: later publishes keep it, so columns never move under the user.
    const columnOrderRef = React.useRef<string[] | undefined>(undefined);
    // The rows on screen, for the join's keep-rows check, and how many of them the grid has
    // rendered (reported by the host through markRowsRendered).
    const emittedRowsRef = React.useRef<any[]>([]);
    const renderedRowsRef = React.useRef<number>(0);

    // Full identity of the library set: driveIds often resolve asynchronously after the
    // listIds are known, and rows must re-augment when they land (Graph commands read
    // the per-row driveId). The schema cache keys on listId+webUrl only — fields don't
    // depend on driveId, so its late arrival must not refetch every schema.
    const librariesKey = React.useMemo(
        () => (libraries ?? []).map(library => `${getAggregatedSourceKey(library)}:${library.webUrl}:${library.driveId ?? ""}`).sort().join("|"),
        [libraries]
    );
    // Schemas are per LIST, not per source: two folder-scoped sources on one list
    // share fields/views, so the key (and the stage-1 fan-out) dedupes to lists.
    const schemaKey = React.useMemo(
        () => Array.from(new Set((libraries ?? []).map(library => `${library.listId}:${library.webUrl}`))).sort().join("|"),
        [libraries]
    );
    const groupLibrariesKey = React.useMemo(
        () => (groupLibraries ?? []).map(library => `${getAggregatedSourceKey(library)}:${library.webUrl}:${library.driveId ?? ""}:${library.siteName ?? ""}`).sort().join("|"),
        [groupLibraries]
    );
    const hasGroupSources: boolean = React.useMemo(
        () => validGroupSources(groupLibraries, new Set((libraries ?? []).map(library => String(library?.listId).toLowerCase()))).length > 0,
        [groupLibrariesKey, librariesKey]
    );
    // Derived, so the indicator is on from the very render the site's own rows appear in.
    const isLoadingGroupSources: boolean = hasGroupSources && !isLoading && finishedJoin !== `${stageOneSequence}|${groupLibrariesKey}`;

    // Columns over every known field set (the site's own first, so their keys never change),
    // group libraries as non-voters, in the order already on screen.
    const composeColumns = (fieldSets: ILibraryFieldSet[], ownViewOrders: IAggregatedViewOrders, groupListIds: string[]): { merged: IAggregatedColumn[]; viewHidden: Set<string> } => {
        const viewOrders = withNonVotingViewOrders(ownViewOrders, groupListIds);
        const merged = pinAggregatedColumnOrder(columnOrderRef.current, orderColumnsByViewStats(mergeAggregatedColumns(fieldSets), viewOrders));
        return { merged, viewHidden: getViewHiddenColumnKeys(merged, viewOrders) };
    };

    const publishColumns = (merged: IAggregatedColumn[], viewHidden: Set<string>): void => {
        columnOrderRef.current = merged.map(column => column.key);
        setMergedColumns(merged);
        setViewHiddenColumnKeys(viewHidden);
    };

    // Group field sets already read, one per list (two sources on one list share it).
    const knownGroupFieldSets = (groupSources: IAggregatedLibraryDescriptor[]): ILibraryFieldSet[] =>
        uniqueGroupLists(groupSources)
            .map(library => groupSchemaCacheRef.current.get(groupSchemaKey(library)))
            .filter(Boolean);

    const resolveSort = (merged: IAggregatedColumn[], sortState: IAggregatedSortState, primaryListIds?: Set<string>): ISortResolution => {
        const fixed = FIXED_SORT_INTERNAL_NAMES[sortState.columnKey];
        if (fixed) {
            return {
                spec: { descending: sortState.descending, numeric: fixed.numeric },
                bindings: {},
                isDefault: sortState.columnKey === AGGREGATED_DEFAULT_SORT_KEY,
                fixedInternalName: fixed.internalName,
            };
        }

        const mergedColumn = merged.find(column => `agg_${column.key}` === sortState.columnKey);
        if (!mergedColumn) {
            // Unknown column (e.g. schema changed): fall back to the default sort.
            return { spec: { descending: true, numeric: true }, bindings: {}, isDefault: true, fixedInternalName: "Modified" };
        }

        // Numeric ranking only when every deciding binding agrees it is numeric. On a type
        // conflict the sort runs in string mode and the NUMERIC bindings are dropped
        // (their libraries rank as missing): a numerically server-sorted page is not
        // monotonic under string comparison, so keeping those bindings would break the
        // frontier invariant unboundedly ("99" vs "100"), not just near ties. The site's
        // own libraries decide; a client-group library can only drop out, never flip it.
        const { numeric, bindings } = resolveColumnSortBindings(mergedColumn, primaryListIds);
        return { spec: { descending: sortState.descending, numeric }, bindings, isDefault: false, columnKey: mergedColumn.key };
    };

    // RenderListDataAsStream emits date/number cells as WEB-LOCALE display strings
    // ("24-7-2026", "1.234,56") plus a dotted raw twin (`"Modified."`: ISO / invariant
    // number). Ranking MUST prefer the raw twin — the display string is unparseable
    // outside en-US and would degrade every row to a missing rank.
    const readRankSource = (row: any, internalName: string): any =>
        row?.[`${internalName}.`] ?? row?.[internalName];

    const rankRow = (row: any, listId: string, resolution: ISortResolution): AggregatedSortRank => {
        if (resolution.isDefault) {
            return toSortValue(readRankSource(row, "Modified"));
        }
        if (resolution.fixedInternalName) {
            const binding: IAggregatedColumnBinding = {
                listId,
                internalName: resolution.fixedInternalName,
                type: resolution.spec.numeric ? "DateTime" : "Text",
            };
            const raw = resolution.fixedInternalName === "Editor"
                ? (Array.isArray(row?.Editor) ? row.Editor[0]?.title : undefined)
                : readRankSource(row, resolution.fixedInternalName);
            return toBindingRank(raw, binding);
        }
        const binding = resolution.bindings[listId];
        return toBindingRank(binding ? readRankSource(row, binding.internalName) : undefined, binding);
    };

    const augmentRows = (library: IAggregatedLibraryDescriptor, pageRows: any[], resolution: ISortResolution): any[] =>
        (pageRows ?? []).map(row => {
            const uniqueId: string = row?.UniqueId?.replace("{", "")?.replace("}", "");
            const spItemUrl: string = row?.[".spItemUrl"];
            const itemId = spItemUrl ? spItemUrl.split("/").pop()?.split("?")[0] : undefined;
            let absoluteFileUrl: string;
            try {
                absoluteFileUrl = row?.FileRef ? new URL(library.webUrl).origin + row.FileRef : undefined;
            } catch {
                absoluteFileUrl = undefined;
            }

            return {
                ...row,
                [AGGREGATE_SOURCE_LIST_ID]: library.listId,
                [AGGREGATE_SOURCE_KEY]: getAggregatedSourceKey(library),
                [AGGREGATE_SOURCE_LIBRARY_NAME]: library.libraryName,
                [AGGREGATE_SOURCE_WEB_URL]: library.webUrl,
                [AGGREGATE_SOURCE_SITE_NAME]: library.siteName,
                [AGGREGATE_ROW_KEY]: getAggregatedRowKey(library.listId, row),
                [AGGREGATE_SORT_VALUE]: toSortValue(readRankSource(row, "Modified")),
                [AGGREGATE_ROW_RANK]: rankRow(row, library.listId, resolution),
                // Identity fields the shared panels resolve per item (share panel's
                // resolveSharePointIdentity, rename modal's advanced-search mode,
                // preview panel's SearchSource.Api mode) — so those panels target the
                // row's OWN library instead of the Redux current directory.
                driveId: library.driveId,
                driveid: library.driveId,
                itemId,
                itemid: itemId,
                uniqueid: uniqueId,
                sharePointListId: library.listId,
                serverrelativepath: row?.FileRef,
                serverRelativePath: row?.FileRef,
                name: row?.FileLeafRef,
                weburl: absoluteFileUrl,
                contenttypeid: row?.ContentTypeId,
                currentLocation: { webUrl: library.webUrl },
                ".fileType": row?.[".fileType"] ?? row?.File_x0020_Type,
            };
        });

    const applyPage = (cursor: ILibraryCursor, pageRows: any[], nextHref: string | null): void => {
        const augmented = augmentRows(cursor.library, pageRows, sortResolutionRef.current);
        // lastFetchedRank/exhausted derive from the RAW server page; the buffer append
        // dedupes by row key because refreshItems can re-rank a buffered row into a
        // cursor's unfetched region, where a later page returns it a second time.
        if (augmented.length > 0) {
            cursor.lastFetchedRank = augmented[augmented.length - 1][AGGREGATE_ROW_RANK];
        }
        const existingKeys = new Set(cursor.rows.map(row => row[AGGREGATE_ROW_KEY]));
        cursor.rows = cursor.rows.concat(augmented.filter(row => !existingKeys.has(row[AGGREGATE_ROW_KEY])));
        cursor.nextHref = nextHref;
        cursor.exhausted = !nextHref || augmented.length === 0;
    };

    const totalFetchedRows = (): number => {
        let total = 0;
        cursorsRef.current.forEach(cursor => { total += cursor.rows.length; });
        return total;
    };

    // The state's `listId` field carries the SOURCE KEY (the cursor map key): the
    // frontier math only round-trips it as an opaque cursor identity, and a bare list
    // id would collapse two same-list sources into one frontier entry.
    const cursorStates = (): IAggregatedCursorState[] =>
        Array.from(cursorsRef.current.entries()).map(([sourceKey, cursor]) => ({
            listId: sourceKey,
            exhausted: cursor.exhausted,
            lastFetchedRank: cursor.lastFetchedRank,
        }));

    // Rebuilds the merged buffer and emits the frontier-safe prefix. The buffer only
    // grows within one sort/search generation and the ordering is deterministic (rank,
    // then row key), so already-emitted rows never reorder as more pages arrive.
    const mergeAndEmit = (): void => {
        // A first paint held for the client group: the rows show when the hold ends.
        if (holdRef.current) {
            return;
        }
        const { buffer, emitCount, cursors } = mergeBuffer();
        const emitted = buffer.slice(0, emitCount);
        emittedRowsRef.current = emitted;
        setRows(emitted);
        setHasMore(emitCount < buffer.length || cursors.some(cursor => !cursor.exhausted));
    };

    /** Ends the hold of load `sequence`; false when it is not holding (any more). */
    const releaseHold = (sequence: number): boolean => {
        const hold = holdRef.current;
        if (!hold || hold.sequence !== sequence) {
            return false;
        }
        clearTimeout(hold.timer);
        holdRef.current = null;
        return true;
    };

    /** The held first paint of load `sequence` goes ahead with the rows there are. */
    const endHold = (sequence: number): void => {
        if (!releaseHold(sequence)) {
            return;
        }
        unstable_batchedUpdates(() => {
            mergeAndEmit();
            setIsLoading(false);
        });
        measureAggregatedLoad("painted");
    };

    const mergeBuffer = (): { buffer: any[]; emitCount: number; cursors: ILibraryCursor[] } => {
        const spec = sortResolutionRef.current.spec;
        const cursors = Array.from(cursorsRef.current.values());

        // Kept as a full sort rather than an incremental k-way merge, on measurement:
        // every cursor's rows arrive server-sorted, so the concatenation is a set of
        // sorted runs and V8's TimSort finishes a 5000-row buffer in 0.09-0.14 ms. An
        // incremental merge would mean holding a second structure in sync with the
        // cursors for no measurable gain — the re-render this triggers costs far more.
        // The tiebreak is where the real cost was; see compareAggregateRowKeys.
        const buffer: any[] = cursors
            .flatMap(cursor => cursor.rows)
            .sort((a, b) =>
                compareSortRanks(a[AGGREGATE_ROW_RANK], b[AGGREGATE_ROW_RANK], spec)
                || compareAggregateRowKeys(a[AGGREGATE_ROW_KEY], b[AGGREGATE_ROW_KEY]));

        const emitCount = countEmittableRows(buffer.map(row => row[AGGREGATE_ROW_RANK]), cursorStates(), spec);
        return { buffer, emitCount, cursors };
    };

    /**
     * Fetches the next page of every cursor blocking the merge frontier (the load-more step,
     * shared with the group join's catch-up). Failures mark the cursor exhausted, so it can
     * never block the frontier forever, and badge its source.
     */
    const advanceFrontier = async (accessToken: string, isStale: () => boolean): Promise<void> => {
        const frontierCursors = getFrontierListIds(cursorStates(), sortResolutionRef.current.spec)
            .map(sourceKey => cursorsRef.current.get(sourceKey))
            .filter(Boolean) as ILibraryCursor[];

        const pageResults = await runWithConcurrency(
            frontierCursors,
            cursor => fetchAggregatedDocumentsPage(accessToken, cursor.library, cursor.viewXml, cursor.nextHref, cursor.pageOptions),
            { concurrency: FETCH_CONCURRENCY, isCancelled: isStale }
        );
        if (isStale()) return;

        pageResults.forEach(result => {
            const sourceKey = getAggregatedSourceKey(result.item.library);
            const cursor = result.item;
            // The page belongs to the cursor that asked for it. A group join that ran while it was
            // out may have replaced that cursor under the same key (or dropped it): the new cursor
            // has its own place in the library, and this page must not move it.
            if (cursorsRef.current.get(sourceKey) !== cursor) return;
            if (result.status === "fulfilled") {
                applyPage(cursor, result.value.rows, result.value.nextHref);
            } else {
                // A cursor that stops paging must not block the frontier forever.
                cursor.exhausted = true;
                const reason = classifyAggregatedFailure(result.error);
                const libraryName = cursor.isGroupSource ? groupSourceLabel(cursor.library) : cursor.library.libraryName;
                setFailures(current => current.some(failure => failure.sourceKey === sourceKey)
                    ? current
                    : [...current, { sourceKey, listId: cursor.library.listId, libraryName, reason }]);
            }
        });
    };

    React.useEffect(() => {
        const sequence = ++requestSequence.current;
        const isStale = (): boolean => sequence !== requestSequence.current;

        // Bump the sequence on unmount/re-run so in-flight work from this run is dropped, the
        // hold of its first paint with it.
        const cleanup = (): void => {
            requestSequence.current++;
            releaseHold(sequence);
        };

        markAggregatedLoadStart();
        cursorsRef.current = new Map();
        isLoadingMoreRef.current = false;
        setIsLoading(true);
        setIsLoadingMore(false);
        setRows([]);
        setHasMore(false);
        setIsCapped(false);
        emittedRowsRef.current = [];
        renderedRowsRef.current = 0;
        stageOneRef.current = null;

        // De-overlap is load-bearing, not hygiene: page dedupe is per cursor and the
        // merge assumes a document lives in at most one cursor, so overlapping sources
        // (a folder dossier inside an aggregated library, duplicate dossiers) would
        // render duplicate rows.
        const validLibraries = dedupeAggregatedSourceDescriptors(
            (libraries ?? []).filter(library => library?.listId && library?.webUrl));
        const ownListIds = new Set(validLibraries.map(library => String(library.listId).toLowerCase()));
        // Client-group libraries whose field lists are already known take part in the columns
        // and the sort from the start, so a later load never drops or moves their columns.
        const knownGroupSources = validGroupSources(groupLibrariesRef.current, ownListIds);
        const knownGroupListIds = knownGroupSources.map(library => library.listId);

        // What this first stage decided, handed to the group join (the effect below) as soon as
        // the site's own field lists are read, so the join runs alongside the site's own pages.
        let resolveOwnRows: () => void = () => undefined;
        const ownRowsReady = new Promise<void>(resolve => { resolveOwnRows = resolve; });
        let stageOne: Omit<IStageOneContext, "sequence" | "ownListIds" | "searchValue" | "filters" | "ownRowsReady"> =
            { fieldSets: [], viewOrders: {}, primaryListIds: new Set(), fetchedCount: 0, plannedRows: 0, excluded: [] };
        let isStageOnePublished = false;
        const publishStageOne = (): void => {
            stageOneRef.current = { ...stageOne, sequence, ownListIds, searchValue, filters, ownRowsReady };
            isStageOnePublished = true;
            setStageOneSequence(sequence);
        };

        // The site's own rows are in the merge. They show now - or, when the client group is on
        // its way, with the group's rows when those join, or when the hold runs out.
        const finishOwnRows = (): void => {
            measureAggregatedLoad("own rows");
            if (validGroupSources(groupLibrariesRef.current, ownListIds).length > 0 || isReadingGroupLibrariesRef.current) {
                holdRef.current = { sequence, timer: setTimeout(() => endHold(sequence), GROUP_JOIN_HOLD_MS) };
            } else {
                unstable_batchedUpdates(() => {
                    mergeAndEmit();
                    setIsLoading(false);
                });
                measureAggregatedLoad("painted");
            }
            resolveOwnRows();
        };

        if (validLibraries.length === 0) {
            // Nothing of its own to load; client-group libraries may still join.
            const { merged, viewHidden } = composeColumns(knownGroupFieldSets(knownGroupSources), {}, knownGroupListIds);
            sortResolutionRef.current = resolveSort(merged, sort, new Set());
            publishColumns(merged, viewHidden);
            setFailures([]);
            setExcludedByFilter([]);
            setHasForceCheckoutLibrary(false);
            publishStageOne();
            finishOwnRows();
            return cleanup;
        }

        (async () => {
            try {
                const accessToken = await authModule.getSharePointAccessToken();
                if (isStale()) return;

                // Stage 1: field schemas + default-view field order per library (cached —
                // sort/search/driveId changes reuse them). Both requests run inside one
                // pool slot; the fields fetch decides library readability, the view fetch
                // is fail-open (null → library absent from viewOrders → its columns are
                // shielded from view-based hiding and carry no position measurement).
                let schemaEntry = schemaCacheRef.current;
                if (!schemaEntry || schemaEntry.key !== schemaKey) {
                    // One schema request per LIST: folder-scoped sources share their
                    // list's fields/views, and duplicate fieldSets would double-count
                    // the column merge's presence stats.
                    const schemaLibraries = validLibraries.filter((library, index, all) =>
                        all.findIndex(other => other.listId === library.listId && other.webUrl === library.webUrl) === index);
                    const schemaResults = await runWithConcurrency(
                        schemaLibraries,
                        library => Promise.all([
                            fetchAggregatedLibraryFields(accessToken, library.webUrl, library.listId),
                            fetchAggregatedLibraryViewFields(accessToken, library.webUrl, library.listId),
                        ]),
                        { concurrency: FETCH_CONCURRENCY, isCancelled: isStale }
                    );
                    if (isStale()) return;

                    const schemaFailures: IAggregatedLibraryFailure[] = [];
                    const fieldSets: ILibraryFieldSet[] = [];
                    const readableListIds: string[] = [];
                    const viewOrders: IAggregatedViewOrders = {};

                    schemaResults.forEach(result => {
                        const [fields, viewFields] = result.status === "fulfilled" ? result.value : [null, null];
                        if (Array.isArray(fields)) {
                            readableListIds.push(result.item.listId);
                            fieldSets.push({
                                listId: result.item.listId,
                                fields: fields.map(parseLibraryField).filter(Boolean),
                            });
                            if (viewFields) {
                                viewOrders[result.item.listId] = viewFields;
                            }
                        } else {
                            // Always "unknown": this is the FIELD-SCHEMA fan-out, which
                            // reads list metadata rather than items, so the list view
                            // threshold cannot apply to it. Fanned back out to EVERY
                            // source on the failed list (the request was per list, the
                            // badges are per source).
                            validLibraries
                                .filter(library => library.listId === result.item.listId)
                                .forEach(library => schemaFailures.push({
                                    sourceKey: getAggregatedSourceKey(library),
                                    listId: library.listId,
                                    libraryName: library.libraryName,
                                    reason: "unknown",
                                }));
                        }
                    });

                    schemaEntry = { key: schemaKey, fieldSets, viewOrders, readableListIds, failures: schemaFailures };
                    schemaCacheRef.current = schemaEntry;
                    // A fresh schema is laid out afresh; otherwise the order on screen holds.
                    columnOrderRef.current = undefined;
                }

                const readableSet = new Set(schemaEntry.readableListIds);
                const readableLibraries = validLibraries.filter(library => readableSet.has(library.listId));
                const loadFailures: IAggregatedLibraryFailure[] = [...schemaEntry.failures];
                const { merged, viewHidden } = composeColumns(
                    [...schemaEntry.fieldSets, ...knownGroupFieldSets(knownGroupSources)], schemaEntry.viewOrders, knownGroupListIds);
                const resolution = resolveSort(merged, sort, readableSet);
                sortResolutionRef.current = resolution;

                // Column filters resolve per library; a library that lacks a filtered
                // column is excluded (it cannot contain matching documents).
                const excludedLibraryNames: string[] = [];
                const filterableLibraries = readableLibraries.filter(library => {
                    const whereXml = buildLibraryWhereXml(library, merged, filters);
                    if (whereXml === null) {
                        excludedLibraryNames.push(library.libraryName);
                        return false;
                    }
                    return true;
                });
                setExcludedByFilter(excludedLibraryNames);

                // Stage 2: first documents page per readable library, server-sorted by the
                // active sort where that library binds the sorted column.
                // Sized off the libraries actually being fetched, not the site total:
                // libraries excluded above issue no request, so letting them shrink the
                // page would thin the fan-out for work that never happens.
                const pageSize = resolveAggregatedPageSize(filterableLibraries.length);

                // All the group join needs: it starts now, alongside the site's own pages.
                stageOne = {
                    fieldSets: schemaEntry.fieldSets,
                    viewOrders: schemaEntry.viewOrders,
                    primaryListIds: readableSet,
                    fetchedCount: filterableLibraries.length,
                    plannedRows: filterableLibraries.length * pageSize,
                    excluded: excludedLibraryNames,
                };
                publishStageOne();

                const pageResults = await runWithConcurrency(
                    filterableLibraries,
                    library => {
                        const boundInternalNames = merged
                            .map(column => column.bindings[library.listId]?.internalName)
                            .filter(Boolean);
                        const whereXml = buildLibraryWhereXml(library, merged, filters) ?? "";
                        // Stored on the cursor and reused by every later page for this
                        // library, so load-more keeps the same depth.
                        const viewXml = buildAggregatedViewXml(boundInternalNames, whereXml, pageSize);
                        const sortBinding = resolution.fixedInternalName ?? resolution.bindings[library.listId]?.internalName;
                        const pageOptions: IAggregatedPageOptions = {
                            sortFieldInternalName: sortBinding,
                            // MUST mirror the client merge spec exactly — the frontier
                            // invariant assumes server page order == client rank order.
                            sortDescending: resolution.spec.descending,
                            searchValue,
                        };
                        const cursor: ILibraryCursor = {
                            library,
                            viewXml,
                            pageOptions,
                            nextHref: null,
                            exhausted: true,
                            lastFetchedRank: null,
                            rows: [],
                        };
                        cursorsRef.current.set(getAggregatedSourceKey(library), cursor);
                        return fetchAggregatedDocumentsPage(accessToken, library, viewXml, null, pageOptions);
                    },
                    { concurrency: FETCH_CONCURRENCY, isCancelled: isStale }
                );
                if (isStale()) return;

                pageResults.forEach(result => {
                    const sourceKey = getAggregatedSourceKey(result.item);
                    const cursor = cursorsRef.current.get(sourceKey);
                    if (result.status === "fulfilled" && cursor) {
                        applyPage(cursor, result.value.rows, result.value.nextHref);
                    } else {
                        cursorsRef.current.delete(sourceKey);
                        loadFailures.push({
                            sourceKey,
                            listId: result.item.listId,
                            libraryName: result.item.libraryName,
                            reason: classifyAggregatedFailure(result.error),
                        });
                    }
                });

                publishColumns(merged, viewHidden);
                setFailures(loadFailures);
                // MONOTONIC on purpose: a library's ForceCheckout setting cannot change
                // mid-session, and this flag only gates the realtime connection. Letting
                // it fall back to false would tear the socket down whenever a filter
                // excluded the force-checkout library from the page fan-out — and rebuild
                // it when the filter cleared.
                setHasForceCheckoutLibrary(current =>
                    current || pageResults.some(result => result.status === "fulfilled" && result.value.isForceCheckout));
                // The initial fan-out can overshoot the cap when the site has many
                // libraries (N × page size); surface it immediately so the badge is
                // truthful — the next loadMore call stops paging.
                if (totalFetchedRows() >= AGGREGATED_MAX_TOTAL_ROWS) {
                    setIsCapped(true);
                }
            } catch (error) {
                logger(error, "useAggregatedDocuments.ts (load)");
                if (!isStale()) {
                    // Whole-load failure (token acquisition, or anything the per-library
                    // fan-out did not already absorb), so every library is reported.
                    // Classified from the real error rather than hard-coded: the reason
                    // is shared by all of them here, whatever it turns out to be.
                    const reason = classifyAggregatedFailure(error);
                    setFailures(validLibraries.map(library => ({
                        sourceKey: getAggregatedSourceKey(library),
                        listId: library.listId,
                        libraryName: library.libraryName,
                        reason,
                    })));
                }
            }
            if (!isStale()) {
                // A load that failed before its field lists were read still lets the group join.
                if (!isStageOnePublished) {
                    publishStageOne();
                }
                finishOwnRows();
            }
        })();

        return cleanup;
    }, [librariesKey, refreshToggle, sort.columnKey, sort.descending, searchValue, filtersKey]);

    /**
     * Stage two: the client-group libraries join the load as soon as its first stage has read the
     * site's own field lists, alongside the site's own pages, and again whenever the group
     * libraries change (they may arrive after the site's own). The rows merge once the site's
     * own are in. Each run replaces the previous join's cursors and badges.
     */
    React.useEffect(() => {
        const stageOneContext = stageOneRef.current;
        if (!stageOneContext || stageOneContext.sequence !== requestSequence.current) {
            // The current load is still reading the site's own field lists; it re-triggers this then.
            return undefined;
        }
        const joinSequence = ++groupJoinSequenceRef.current;
        const { sequence, ownRowsReady } = stageOneContext;
        const isStale = (): boolean => sequence !== requestSequence.current || joinSequence !== groupJoinSequenceRef.current;
        const joinKey = `${stageOneSequence}|${groupLibrariesKey}`;

        const groupSources = validGroupSources(groupLibraries, stageOneContext.ownListIds);

        // A previous join's cursors and badges go (the group set changed) - its badges even when it
        // left no cursor behind (every one of its libraries failed). Badges this join adds while
        // paging (advanceFrontier) are kept by the final merge below.
        let removedAny = false;
        cursorsRef.current.forEach((cursor, sourceKey) => {
            if (cursor.isGroupSource) {
                cursorsRef.current.delete(sourceKey);
                removedAny = true;
            }
        });
        setFailures(current => {
            const own = current.filter(failure => stageOneContext.ownListIds.has(String(failure.listId).toLowerCase()));
            return own.length === current.length ? current : own;
        });
        if (removedAny) {
            setExcludedByFilter(stageOneContext.excluded);
            mergeAndEmit();
        }
        if (groupSources.length === 0) {
            return undefined;
        }

        (async () => {
            try {
                const accessToken = await authModule.getSharePointAccessToken();
                if (isStale()) return;

                // Field lists only: group libraries do not vote on the columns (no default-view
                // read), so the columns and their visibility stay the site's own. Usually already
                // read, or on their way (the session cache, filled as soon as the libraries are known).
                // After a refresh every one is read again: the first stage sorted with the lists it
                // knew, and they may have changed since.
                const rereadSchemas: boolean = groupSchemaStaleRef.current;
                const unread = uniqueGroupLists(groupSources).filter(library => rereadSchemas || !groupSchemaCacheRef.current.has(groupSchemaKey(library)));
                if (unread.length > 0) {
                    const schemaResults = await runWithConcurrency(
                        unread,
                        library => readClientGroupLibraryFields(accessToken, library),
                        { concurrency: GROUP_FETCH_CONCURRENCY, isCancelled: isStale }
                    );
                    if (isStale()) return;
                    schemaResults.forEach(result => {
                        groupSchemaCacheRef.current.set(groupSchemaKey(result.item),
                            result.status === "fulfilled" && Array.isArray(result.value)
                                ? { listId: result.item.listId, fields: result.value.map(parseLibraryField).filter(Boolean) }
                                : null);
                    });
                }
                if (rereadSchemas) {
                    groupSchemaStaleRef.current = false;
                }

                const groupFailures: IAggregatedLibraryFailure[] = [];
                const toFailure = (library: IAggregatedLibraryDescriptor, reason: AggregatedFailureReason): IAggregatedLibraryFailure =>
                    ({ sourceKey: getAggregatedSourceKey(library), listId: library.listId, libraryName: groupSourceLabel(library), reason });
                const readable = groupSources.filter(library => {
                    if (groupSchemaCacheRef.current.get(groupSchemaKey(library))) {
                        return true;
                    }
                    groupFailures.push(toFailure(library, "unknown"));
                    return false;
                });

                const composeJoinedColumns = () => composeColumns(
                    [...stageOneContext.fieldSets, ...knownGroupFieldSets(readable)],
                    stageOneContext.viewOrders,
                    readable.map(library => library.listId));
                const { merged } = composeJoinedColumns();

                // The load's own sort, extended to the group libraries that are compatible with
                // it; a fixed or fallen-back sort needs no bindings. The group libraries' bindings
                // come from the field lists read now, never from the ones the first stage knew.
                const resolution: ISortResolution = { ...sortResolutionRef.current, bindings: { ...sortResolutionRef.current.bindings } };
                groupSources.forEach(library => { delete resolution.bindings[library.listId]; });
                const sortColumn = resolution.columnKey ? merged.find(column => column.key === resolution.columnKey) : undefined;
                if (resolution.columnKey && !sortColumn) {
                    // No library has the sorted column any more (a refresh found it deleted): the
                    // view falls back to the default sort, so the header says what the rows do.
                    setSort({ columnKey: AGGREGATED_DEFAULT_SORT_KEY, descending: true });
                    return;
                }
                if (sortColumn) {
                    const compatible = resolveColumnSortBindings(sortColumn, stageOneContext.primaryListIds).bindings;
                    readable.forEach(library => {
                        const binding = compatible[library.listId];
                        if (binding && isNumericRankType(binding) === resolution.spec.numeric) {
                            resolution.bindings[library.listId] = binding;
                        }
                    });
                }
                sortResolutionRef.current = resolution;

                const excluded: string[] = [];
                const filterable = readable.filter(library => {
                    if (buildLibraryWhereXml(library, merged, stageOneContext.filters) === null) {
                        excluded.push(groupSourceLabel(library));
                        return false;
                    }
                    return true;
                });

                // The site's own first pages may still be on their way: count what they can bring.
                const pageSize = resolveGroupSourcePageSize(
                    stageOneContext.fetchedCount + filterable.length, Math.max(totalFetchedRows(), stageOneContext.plannedRows), filterable.length);
                const pageResults = await runWithConcurrency(
                    filterable,
                    async library => {
                        const boundInternalNames = merged
                            .map(column => column.bindings[library.listId]?.internalName)
                            .filter(Boolean);
                        const viewXml = buildAggregatedViewXml(boundInternalNames, buildLibraryWhereXml(library, merged, stageOneContext.filters) ?? "", pageSize);
                        const pageOptions: IAggregatedPageOptions = {
                            sortFieldInternalName: resolution.fixedInternalName ?? resolution.bindings[library.listId]?.internalName,
                            sortDescending: resolution.spec.descending,
                            searchValue: stageOneContext.searchValue,
                        };
                        const firstPage = await fetchAggregatedDocumentsPage(accessToken, library, viewXml, null, pageOptions);
                        return { firstPage, viewXml, pageOptions };
                    },
                    { concurrency: GROUP_FETCH_CONCURRENCY, isCancelled: isStale }
                );
                if (isStale()) return;
                await ownRowsReady;
                if (isStale()) return;

                // The group cursors are STAGED, outside the merge, until the rows on screen are
                // safe: nothing (a realtime update, a delete, a load-more) can publish the merge
                // half-way, with rows shown a moment ago missing from the end.
                const staged: Map<string, ILibraryCursor> = new Map<string, ILibraryCursor>();
                pageResults.forEach(result => {
                    if (result.status === "fulfilled") {
                        const cursor: ILibraryCursor = {
                            library: result.item,
                            viewXml: result.value.viewXml,
                            pageOptions: result.value.pageOptions,
                            nextHref: null,
                            exhausted: true,
                            lastFetchedRank: null,
                            rows: [],
                            isGroupSource: true,
                        };
                        staged.set(getAggregatedSourceKey(result.item), cursor);
                        applyPage(cursor, result.value.firstPage.rows, result.value.firstPage.nextHref);
                    } else {
                        groupFailures.push(toFailure(result.item, classifyAggregatedFailure(result.error)));
                    }
                });

                // What is on screen now, which the join must keep there: nothing while the first
                // paint is held, so a held join pages nothing ahead (load-more brings the rest in
                // below the rows it shows).
                const shown = emittedRowsRef.current;
                const keepCount = Math.min(shown.length, GROUP_JOIN_KEEP_ROWS, Math.max(renderedRowsRef.current, GROUP_JOIN_MIN_KEEP_ROWS));
                if (keepCount > 0) {
                    await keepRowsOnScreen(accessToken, shown[keepCount - 1][AGGREGATE_ROW_RANK], staged, groupFailures, isStale);
                    if (isStale()) return;
                }

                staged.forEach((cursor, sourceKey) => cursorsRef.current.set(sourceKey, cursor));
                // Past the row cap nothing may page any more - load-more's rule: every cursor is
                // exhausted, so the whole buffer shows instead of rows hiding behind a frontier.
                const isAtCap: boolean = totalFetchedRows() >= AGGREGATED_MAX_TOTAL_ROWS;
                if (isAtCap) {
                    cursorsRef.current.forEach(cursor => { cursor.exhausted = true; });
                }

                // Columns in the order on screen now (the site's own rows may have shown meanwhile).
                const joined = composeJoinedColumns();
                // One render for the whole merge: it is the moment rows move - or, when the first
                // paint was held for it, the moment every row shows.
                let wasHeld = false;
                unstable_batchedUpdates(() => {
                    wasHeld = releaseHold(sequence);
                    publishColumns(joined.merged, joined.viewHidden);
                    setFailures(current => {
                        const known = new Set(current.map(failure => failure.sourceKey));
                        return [...current, ...groupFailures.filter(failure => !known.has(failure.sourceKey))];
                    });
                    setExcludedByFilter([...stageOneContext.excluded, ...excluded]);
                    setHasForceCheckoutLibrary(current =>
                        current || pageResults.some(result => result.status === "fulfilled" && result.value.firstPage.isForceCheckout));
                    mergeAndEmit();
                    if (isAtCap) {
                        setIsCapped(true);
                    }
                    if (wasHeld) {
                        setIsLoading(false);
                    }
                    // The indicator goes in the same render the rows arrive in.
                    setFinishedJoin(joinKey);
                });
                measureAggregatedLoad("group joined");
                if (wasHeld) {
                    measureAggregatedLoad("painted");
                }
            } catch (error) {
                logger(error, "useAggregatedDocuments.ts (group join)");
            }
            // A join that failed still lets a held first paint go ahead, once the site's own rows are in.
            await ownRowsReady;
            if (!isStale()) {
                setFinishedJoin(joinKey);
                endHold(sequence);
            }
        })();

        return () => {
            groupJoinSequenceRef.current++;
        };
    }, [stageOneSequence, groupLibrariesKey]);

    // The client group's field lists are read as soon as its libraries are known, while the site's
    // own load is still reading its own: the join then finds them in the session cache, or shares
    // the read already under way.
    React.useEffect(() => {
        const ownListIds = new Set((libraries ?? []).map(library => String(library?.listId).toLowerCase()));
        const groupSources = uniqueGroupLists(validGroupSources(groupLibraries, ownListIds));
        if (groupSources.length === 0) {
            return undefined;
        }
        let isCancelled = false;
        (async () => {
            try {
                const accessToken = await authModule.getSharePointAccessToken();
                if (!isCancelled) {
                    await runWithConcurrency(groupSources, library => readClientGroupLibraryFields(accessToken, library),
                        { concurrency: GROUP_FETCH_CONCURRENCY, isCancelled: () => isCancelled });
                }
            } catch (error) {
                logger(error, "useAggregatedDocuments.ts (group field lists)");
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [groupLibrariesKey, librariesKey, refreshToggle]);

    // The client group's libraries turned out to be none (or went): a first paint held for them
    // goes ahead.
    React.useEffect(() => {
        const hold = holdRef.current;
        if (hold && !isReadingGroupLibraries && !hasGroupSources) {
            endHold(hold.sequence);
        }
    }, [isReadingGroupLibraries, hasGroupSources]);

    /**
     * Before a group join publishes: pages the STAGED group cursors that could still hold a row
     * sorting at or before `keepRank` (the last row kept on screen, up to GROUP_JOIN_KEEP_ROWS
     * down), every one of them each round, until none can - so the rows on screen stay emittable
     * once the group cursors join. Bounded by GROUP_JOIN_MAX_ROUNDS and the row cap; whatever
     * is still short refills through load-more.
     *
     * Only group cursors can block: the kept row was emitted, so it already sorts before every
     * own cursor's last fetched row, and own cursors only ever move further on. That is also why
     * this needs no load-more lock: it never touches a cursor load-more can page.
     *
     * Known limit (accepted): under an ASCENDING sort on a metadata column, a group library that
     * lacks the column (or binds it as the other kind) ranks every row as empty, and empty sorts
     * first ascending. If the rounds do not exhaust it, the rows on screen move below its rows
     * until load-more pages it out. The order is still right: the site's own libraries without
     * the column sort the same way.
     */
    const keepRowsOnScreen = async (
        accessToken: string,
        keepRank: AggregatedSortRank,
        staged: Map<string, ILibraryCursor>,
        failures: IAggregatedLibraryFailure[],
        isStale: () => boolean
    ): Promise<void> => {
        const stagedRows = (): number => Array.from(staged.values()).reduce((total, cursor) => total + cursor.rows.length, 0);
        for (let round = 0; round < GROUP_JOIN_MAX_ROUNDS; round++) {
            const spec = sortResolutionRef.current.spec;
            const blocking = Array.from(staged.values())
                .filter(cursor => !cursor.exhausted && compareSortRanks(keepRank, cursor.lastFetchedRank, spec) >= 0);
            if (blocking.length === 0 || totalFetchedRows() + stagedRows() >= AGGREGATED_MAX_TOTAL_ROWS) {
                return;
            }
            const pageResults = await runWithConcurrency(
                blocking,
                cursor => fetchAggregatedDocumentsPage(accessToken, cursor.library, cursor.viewXml, cursor.nextHref, cursor.pageOptions),
                { concurrency: GROUP_FETCH_CONCURRENCY, isCancelled: isStale }
            );
            if (isStale()) return;
            pageResults.forEach(result => {
                if (result.status === "fulfilled") {
                    applyPage(result.item, result.value.rows, result.value.nextHref);
                } else {
                    // Stops paging, so it can never hold the frontier; badged like any failure.
                    result.item.exhausted = true;
                    failures.push({
                        sourceKey: getAggregatedSourceKey(result.item.library),
                        listId: result.item.library.listId,
                        libraryName: groupSourceLabel(result.item.library),
                        reason: classifyAggregatedFailure(result.error),
                    });
                }
            });
        }
    };

    const loadMore = React.useCallback(() => {
        const sequence = requestSequence.current;
        const isStale = (): boolean => sequence !== requestSequence.current;

        if (isLoadingMoreRef.current) {
            return;
        }

        const spec = sortResolutionRef.current.spec;
        // Source keys, not list ids — cursorStates() feeds the map keys through.
        const frontierListIds = getFrontierListIds(cursorStates(), spec);
        if (frontierListIds.length === 0) {
            mergeAndEmit();
            return;
        }

        if (totalFetchedRows() >= AGGREGATED_MAX_TOTAL_ROWS) {
            cursorsRef.current.forEach(cursor => { cursor.exhausted = true; });
            setIsCapped(true);
            mergeAndEmit();
            return;
        }

        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);

        (async () => {
            try {
                const accessToken = await authModule.getSharePointAccessToken();
                if (isStale()) return;

                await advanceFrontier(accessToken, isStale);
                if (isStale()) return;

                mergeAndEmit();
            } catch (error) {
                logger(error, "useAggregatedDocuments.ts (loadMore)");
            }
            if (!isStale()) {
                setIsLoadingMore(false);
            }
            isLoadingMoreRef.current = false;
        })();
    }, []);

    /** The grid reports rows it has rendered (the highest count seen this load wins). */
    const markRowsRendered = React.useCallback((count: number): void => {
        if (Number.isFinite(count) && count > renderedRowsRef.current) {
            renderedRowsRef.current = count;
        }
    }, []);

    const setSortColumn = React.useCallback((columnKey: string) => {
        setSort(current => ({
            columnKey,
            descending: current.columnKey === columnKey ? !current.descending : true,
        }));
    }, []);

    const refresh = React.useCallback(() => {
        // Refresh must re-read schemas too (a column may have been added/renamed) - the
        // client-group libraries' as well, session cache included. Theirs are only marked stale:
        // the first stage still sorts by a column only they have, and the join reads them again.
        schemaCacheRef.current = null;
        groupSchemaStaleRef.current = true;
        clearClientGroupLibraryFieldsCache();
        setRefreshToggle(current => !current);
    }, []);

    /**
     * Recycles the given rows (grouped per source library) and removes the successful
     * ones from the merged buffer. Returns the number of items that FAILED to delete.
     */
    const deleteItems = React.useCallback(async (items: any[]): Promise<number> => {
        const sequence = requestSequence.current;
        const deletable = (items ?? []).filter(item => item?.[AGGREGATE_SOURCE_LIST_ID] && item?.ID);
        if (deletable.length === 0) {
            return 0;
        }

        let failedCount = 0;
        try {
            const accessToken = await authModule.getSharePointAccessToken();

            const results = await runWithConcurrency(
                deletable,
                item => {
                    const cursor = cursorsRef.current.get(item[AGGREGATE_SOURCE_KEY] ?? item[AGGREGATE_SOURCE_LIST_ID]);
                    const library: IAggregatedLibraryDescriptor = cursor?.library ?? {
                        listId: item[AGGREGATE_SOURCE_LIST_ID],
                        libraryName: item[AGGREGATE_SOURCE_LIBRARY_NAME],
                        webUrl: item[AGGREGATE_SOURCE_WEB_URL],
                    };
                    return recycleAggregatedItem(accessToken, library, item.ID);
                },
                { concurrency: FETCH_CONCURRENCY }
            );

            const deletedKeys = new Set(
                results.filter(result => result.status === "fulfilled").map(result => result.item[AGGREGATE_ROW_KEY]));
            failedCount = results.length - deletedKeys.size;

            if (sequence === requestSequence.current && deletedKeys.size > 0) {
                cursorsRef.current.forEach(cursor => {
                    cursor.rows = cursor.rows.filter(row => !deletedKeys.has(row[AGGREGATE_ROW_KEY]));
                });
                mergeAndEmit();
            }
        } catch (error) {
            logger(error, "useAggregatedDocuments.ts (deleteItems)");
            failedCount = deletable.length;
        }
        return failedCount;
    }, []);

    /**
     * Re-fetches the given rows by ID from their source libraries and replaces them in
     * the merged buffer — the per-row refresh used after checkout/checkin/publish/
     * rename. A row the re-fetch no longer returns (deleted, moved, filtered out) is
     * removed from the grid.
     */
    const refreshItems = React.useCallback(async (items: any[]): Promise<void> => {
        const sequence = requestSequence.current;
        const refreshable = (items ?? []).filter(item => item?.[AGGREGATE_SOURCE_LIST_ID] && item?.ID);
        if (refreshable.length === 0) {
            return;
        }

        try {
            const accessToken = await authModule.getSharePointAccessToken();

            const results = await runWithConcurrency(
                refreshable,
                async item => {
                    const cursor = cursorsRef.current.get(item[AGGREGATE_SOURCE_KEY] ?? item[AGGREGATE_SOURCE_LIST_ID]);
                    if (!cursor) {
                        return { key: item[AGGREGATE_ROW_KEY], row: null };
                    }
                    const freshRow = await fetchAggregatedRowById(accessToken, cursor.library, cursor.viewXml, item.ID);
                    return { key: item[AGGREGATE_ROW_KEY], row: freshRow };
                },
                { concurrency: FETCH_CONCURRENCY }
            );

            if (sequence !== requestSequence.current) {
                return;
            }

            for (const result of results) {
                if (result.status !== "fulfilled") continue;
                const { key, row } = result.value;
                const cursor = cursorsRef.current.get(result.item[AGGREGATE_SOURCE_KEY] ?? result.item[AGGREGATE_SOURCE_LIST_ID]);
                if (!cursor) continue;

                if (row) {
                    const [augmented] = augmentRows(cursor.library, [row], sortResolutionRef.current);
                    cursor.rows = cursor.rows.map(existing => existing[AGGREGATE_ROW_KEY] === key ? augmented : existing);
                } else {
                    cursor.rows = cursor.rows.filter(existing => existing[AGGREGATE_ROW_KEY] !== key);
                }
            }

            mergeAndEmit();
        } catch (error) {
            logger(error, "useAggregatedDocuments.ts (refreshItems)");
        }
    }, []);

    /**
     * Realtime (SignalR ItemHub) row maintenance. Every entry point is scoped to ONE
     * source library: the merged buffer holds rows from many libraries whose list-item
     * IDs collide freely, so a lookup by ID alone would hit the wrong document.
     *
     * A library with no cursor (not part of this view, or its page fetch failed) is
     * ignored — there is nothing on screen to update.
     *
     * Broadcasts arrive per LIST (hub groups are per list id) while cursors are per
     * SOURCE, and one list can back several folder-scoped cursors — so every handler
     * fans the list id out to ALL matching cursors. De-overlap guarantees an item
     * lives in at most one of them; for the others the mutation is a no-op (row not
     * found, or the RootFolder-scoped by-id re-read returns empty).
     */
    const findCursorsByListId = (listId: string): Array<[string, ILibraryCursor]> => {
        const target = String(listId ?? "").toLowerCase();
        return Array.from(cursorsRef.current.entries())
            .filter(([, cursor]) => String(cursor.library.listId).toLowerCase() === target);
    };

    const findCursorRowIndex = (cursor: ILibraryCursor, itemId: string): number =>
        cursor.rows.findIndex(row => row && String(row.ID) === String(itemId));

    // The by-id re-read filters on ID INSTEAD of FSObjType (one FilterField slot), so
    // unlike the page fetch it can hand back a folder or document set. This grid shows
    // documents only — such a row must never be inserted.
    const isFolderRow = (row: any): boolean =>
        row?.FSObjType === "1" || row?.ContentTypeId?.startsWith?.(SharePointFolderContentTypeIds.folder);

    const applyRealtimeRowRefresh = async (sourceKey: string, itemId: string, allowInsert: boolean): Promise<void> => {
        const sequence = requestSequence.current;
        const cursor = cursorsRef.current.get(sourceKey);
        if (!cursor || (!allowInsert && findCursorRowIndex(cursor, itemId) === -1)) {
            return;
        }

        try {
            const accessToken = await authModule.getSharePointAccessToken();
            // The cursor's own ViewXml carries this library's field bindings AND the
            // active column filters, so the re-read row is shaped exactly like the ones
            // already in the buffer, and a row the filters exclude comes back empty.
            const freshRow = await fetchAggregatedRowById(accessToken, cursor.library, cursor.viewXml, itemId);

            // A reload/sort/filter change replaced every cursor while this was in
            // flight: that generation's rows are gone, and its verdict must not be
            // written into the new one.
            if (sequence !== requestSequence.current) {
                return;
            }
            const liveCursor = cursorsRef.current.get(sourceKey);
            if (!liveCursor) {
                return;
            }

            const index = findCursorRowIndex(liveCursor, itemId);
            if (!freshRow) {
                // Deleted, moved out, or filtered out since the broadcast.
                if (index === -1) {
                    return;
                }
                liveCursor.rows = liveCursor.rows.filter((_row, rowIndex) => rowIndex !== index);
            } else {
                if (index === -1 && isFolderRow(freshRow)) {
                    return;
                }
                const [augmented] = augmentRows(liveCursor.library, [freshRow], sortResolutionRef.current);
                liveCursor.rows = index === -1
                    // Appended, never rank-positioned: mergeAndEmit re-sorts the whole
                    // buffer, and the frontier keeps the row unemitted until paging
                    // reaches its rank (lastFetchedRank deliberately untouched).
                    ? [...liveCursor.rows, augmented]
                    : liveCursor.rows.map((row, rowIndex) => rowIndex === index ? augmented : row);
            }

            mergeAndEmit();
        } catch (error) {
            logger(error, "useAggregatedDocuments.ts (applyRealtimeRowRefresh)");
        }
    };

    const applyRealtimeCreate = React.useCallback((listId: string, itemId: string): void => {
        findCursorsByListId(listId).forEach(([sourceKey, cursor]) => {
            // The by-id re-read honours column filters (they live in the ViewXml) but NOT
            // the cross-library search, which is a request-level parameter. Inserting blind
            // would put a row on screen that contradicts the search box, so a searched view
            // leaves the new document to the next refresh. A folder-scoped cursor whose
            // scope does not contain the item gets an empty re-read (RootFolder is on the
            // by-id URL too) and inserts nothing.
            if (cursor.pageOptions.searchValue?.trim()) {
                return;
            }
            void applyRealtimeRowRefresh(sourceKey, itemId, true);
        });
    }, []);

    const applyRealtimeUpdate = React.useCallback((listId: string, itemId: string): void => {
        // Update/unlock only ever touches a row already on screen (parity with the
        // single-library grid's reloadRow) — an update for a row outside the loaded
        // window is not evidence that it belongs in it. The refresh early-returns for
        // every cursor not holding the row, so the fan-out costs no extra requests.
        findCursorsByListId(listId).forEach(([sourceKey]) => {
            void applyRealtimeRowRefresh(sourceKey, itemId, false);
        });
    }, []);

    const applyRealtimeDelete = React.useCallback((listId: string, itemId: string): void => {
        let isChanged = false;
        findCursorsByListId(listId).forEach(([, cursor]) => {
            const remaining = cursor.rows.filter(row => !row || String(row.ID) !== String(itemId));
            if (remaining.length !== cursor.rows.length) {
                cursor.rows = remaining;
                isChanged = true;
            }
        });
        if (isChanged) {
            mergeAndEmit();
        }
    }, []);

    const applyRealtimeLock = React.useCallback((listId: string, itemId: string): void => {
        let isChanged = false;
        findCursorsByListId(listId).forEach(([, cursor]) => {
            cursor.rows = cursor.rows.map(row => {
                if (row && String(row.ID) === String(itemId) && row[rowLockIndicator] !== true) {
                    isChanged = true;
                    return { ...row, [rowLockIndicator]: true };
                }
                return row;
            });
        });
        if (isChanged) {
            mergeAndEmit();
        }
    }, []);

    return {
        isLoading, isLoadingMore, isLoadingGroupSources, markRowsRendered, rows, mergedColumns, viewHiddenColumnKeys, hasMore, isCapped, failures, excludedByFilter,
        hasForceCheckoutLibrary, sort, setSortColumn, searchValue, setSearchValue, filters, setFilters, loadMore, refresh,
        deleteItems, refreshItems, applyRealtimeCreate, applyRealtimeUpdate, applyRealtimeDelete, applyRealtimeLock,
    };
};
